import {
  skillfitPackageStatus,
  skillfitPackageCode,
  skillfitPackageVerify,
  skillfitPackageRead,
  skillfitPackageRecording,
  skillfitPackageResume,
} from '../../util/api';
import { rememberSecret, rememberSession, credentialsFor, forget } from './packageLinkStore';

/**
 * `SkillFitPackagePage` state — the page a client contact opens from a shared SkillFit package link,
 * `/skillfit-package/:shareId#k=<secret>`.
 *
 * This slice holds VIEW STATE ONLY: which state the link is in, what the code request answered, and
 * the package once it is read. The link secret and the verified session are never put here; every
 * thunk reads them from `packageLinkStore` at the moment it calls the server. Signed recording and
 * résumé addresses are returned to the caller and never stored either.
 *
 * Every refusal to open (404) is one answer, whatever the server's reason. A refused read drops the
 * browser's session for the share and re-reads the link's status, so the page shows what the link is
 * now and never why it stopped working; the secret goes too once the link is known to be finished.
 */

// ================ Action types ================ //

export const LINK_STATE = 'app/SkillFitPackagePage/LINK_STATE';
export const CODE_STATE = 'app/SkillFitPackagePage/CODE_STATE';
export const PACKAGE_LOADED = 'app/SkillFitPackagePage/PACKAGE_LOADED';

export const PHASES = ['checking', 'open', 'codeSent', 'package', 'expired', 'unavailable', 'error'];

// ================ Reducer ================ //

const initialCode = {
  sentTo: null,
  codeError: null,
  retryAt: null,
  limitReached: false,
  notSent: false,
  inProgress: false,
};

const initialState = {
  shareId: null,
  phase: 'checking',
  organisationName: null,
  code: initialCode,
  pkg: null,
};

export default function skillFitPackagePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LINK_STATE: {
      const { phase, organisationName, shareId } = payload;
      // Opening a link starts from nothing. Leaving the package phase drops the package, so a share
      // revoked while it is open leaves nothing of the candidate on screen.
      const base = phase === 'checking' ? { ...initialState, shareId: shareId || state.shareId } : state;
      return {
        ...base,
        phase,
        organisationName: organisationName !== undefined ? organisationName : base.organisationName,
        pkg: phase === 'package' ? base.pkg : null,
      };
    }
    case CODE_STATE:
      return { ...state, code: { ...state.code, ...payload } };
    case PACKAGE_LOADED:
      return {
        ...state,
        phase: 'package',
        organisationName: (payload && payload.organisationName) || state.organisationName,
        code: initialCode,
        pkg: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const linkState = payload => ({ type: LINK_STATE, payload });
const codeState = payload => ({ type: CODE_STATE, payload });
const packageLoaded = payload => ({ type: PACKAGE_LOADED, payload });

// ================ Selectors ================ //

export const selectPackagePage = state => state.SkillFitPackagePage || initialState;

// ================ Thunks ================ //

const isRefused = e => Boolean(e) && e.status === 404;

const secretFromFragment = fragment => {
  if (typeof fragment !== 'string' || !fragment) return null;
  const raw = fragment.charAt(0) === '#' ? fragment.slice(1) : fragment;
  try {
    const value = new URLSearchParams(raw).get('k');
    return value || null;
  } catch (e) {
    return null;
  }
};

const shareIdOf = getState => selectPackagePage(getState()).shareId;

/**
 * The link's current state, from the server. `expired` and `ended` also forget the browser: the
 * link will never open again. `open` keeps only the secret, so a code can be asked for.
 */
const loadStatus = (shareId, secret) => dispatch =>
  skillfitPackageStatus({ shareId, secret })
    .then(({ state, organisationName } = {}) => {
      if (state === 'open') {
        dispatch(linkState({ phase: 'open', organisationName: organisationName || null }));
        return;
      }
      forget(shareId);
      if (state === 'expired') {
        dispatch(linkState({ phase: 'expired', organisationName: organisationName || null }));
      } else {
        dispatch(linkState({ phase: 'unavailable', organisationName: null }));
      }
    })
    .catch(e => {
      if (isRefused(e)) {
        forget(shareId);
        dispatch(linkState({ phase: 'unavailable', organisationName: null }));
      } else {
        dispatch(linkState({ phase: 'error' }));
      }
    });

/**
 * A read was refused. The browser drops its session for this share at once, and the page shows what
 * the link is now: expired, no longer available, or — when only this browser's session was dropped —
 * open again for a new code. The secret is forgotten only once the link is known to be finished; if
 * the status itself cannot be read, the page shows an error and keeps the secret, so trying again
 * can still reach the link.
 */
export const packageRefused = () => (dispatch, getState) => {
  const shareId = shareIdOf(getState);
  const creds = credentialsFor(shareId);
  forget(shareId);
  if (!creds) {
    dispatch(linkState({ phase: 'unavailable', organisationName: null }));
    return Promise.resolve();
  }
  // Keep the secret without the refused session until the status says the link is finished.
  rememberSecret(shareId, creds.secret);
  // Leave the package before the status read, so nothing of it stays on screen meanwhile.
  dispatch(linkState({ phase: 'checking', shareId }));
  return dispatch(loadStatus(shareId, creds.secret));
};

const readPackage = ({ shareId, secret, session }) => dispatch =>
  skillfitPackageRead({ shareId, secret, session }).then(pkg => {
    dispatch(packageLoaded(pkg));
    return pkg;
  });

/**
 * Opens the link: the secret from the fragment (or the one this browser kept), then either the
 * package, when this browser is already verified, or the link's status. With no secret at all the
 * link cannot be opened, and nothing is asked of the server.
 */
export const openPackageLink = ({ shareId, fragment }) => dispatch => {
  dispatch(linkState({ phase: 'checking', shareId }));

  const secret = secretFromFragment(fragment);
  if (secret) rememberSecret(shareId, secret);

  const creds = credentialsFor(shareId);
  if (!creds) {
    dispatch(linkState({ phase: 'unavailable', organisationName: null }));
    return Promise.resolve();
  }

  if (creds.session) {
    return dispatch(readPackage({ shareId, secret: creds.secret, session: creds.session })).catch(e => {
      if (isRefused(e)) return dispatch(packageRefused());
      dispatch(linkState({ phase: 'error' }));
      return undefined;
    });
  }
  return dispatch(loadStatus(shareId, creds.secret));
};

const hasAddress = sentTo => Boolean(sentTo && (sentTo.email || sentTo.phone));

export const requestPackageCode = () => (dispatch, getState) => {
  const { code, phase } = selectPackagePage(getState());
  if (code.inProgress) return Promise.resolve();
  const shareId = shareIdOf(getState);
  const creds = credentialsFor(shareId);
  if (!creds) return dispatch(packageRefused());

  dispatch(codeState({ inProgress: true, codeError: null, notSent: false }));
  return skillfitPackageCode({ shareId, secret: creds.secret })
    .then(({ sentTo } = {}) => {
      if (!hasAddress(sentTo)) {
        dispatch(codeState({ inProgress: false, notSent: true }));
        return;
      }
      dispatch(codeState({ inProgress: false, sentTo, retryAt: null }));
      if (phase !== 'codeSent') dispatch(linkState({ phase: 'codeSent' }));
    })
    .catch(e => {
      if (isRefused(e)) {
        dispatch(codeState({ inProgress: false }));
        return dispatch(packageRefused());
      }
      if (e && e.status === 429 && e.error === 'code-throttled') {
        if (e.limitReached) {
          dispatch(codeState({ inProgress: false, limitReached: true, retryAt: null }));
          return undefined;
        }
        const wait = Number(e.retryAfter);
        dispatch(
          codeState({
            inProgress: false,
            retryAt: Date.now() + (Number.isFinite(wait) && wait > 0 ? wait : 60) * 1000,
          })
        );
        // A code went out moments ago, so the recipient may already hold one: offer the field.
        if (phase !== 'codeSent') dispatch(linkState({ phase: 'codeSent' }));
        return undefined;
      }
      dispatch(codeState({ inProgress: false }));
      dispatch(linkState({ phase: 'error' }));
      return undefined;
    });
};

export const verifyPackageCode = code => (dispatch, getState) => {
  if (selectPackagePage(getState()).code.inProgress) return Promise.resolve();
  const shareId = shareIdOf(getState);
  const creds = credentialsFor(shareId);
  if (!creds) return dispatch(packageRefused());

  dispatch(codeState({ inProgress: true, codeError: null }));
  return skillfitPackageVerify({ shareId, secret: creds.secret, code })
    .then(({ session } = {}) =>
      dispatch(readPackage({ shareId, secret: creds.secret, session })).then(pkg => {
        // Remembered only once the package has opened with it, and only until the link expires.
        rememberSession(shareId, session, pkg && pkg.expiresAt);
      })
    )
    .catch(e => {
      dispatch(codeState({ inProgress: false }));
      if (e && e.status === 400 && e.error === 'code-wrong') {
        const remaining = Number.isInteger(e.remaining) ? e.remaining : 0;
        if (remaining <= 0) return dispatch(loadStatus(shareId, creds.secret));
        dispatch(codeState({ codeError: { remaining } }));
        return undefined;
      }
      if (isRefused(e)) return dispatch(packageRefused());
      dispatch(linkState({ phase: 'error' }));
      return undefined;
    });
};

/**
 * A part of an open package: resolves with its signed read, which the caller uses at once and never
 * stores. A refusal replaces the package with the link's current state, and still rejects so the
 * caller stops.
 */
const fetchPart = call => () => (dispatch, getState) => {
  const shareId = shareIdOf(getState);
  const creds = credentialsFor(shareId);
  if (!creds || !creds.session) {
    const refused = Object.assign(new Error('unavailable'), { status: 404, error: 'unavailable' });
    return dispatch(packageRefused()).then(() => Promise.reject(refused));
  }
  return call({ shareId, secret: creds.secret, session: creds.session }).catch(e => {
    if (isRefused(e)) return dispatch(packageRefused()).then(() => Promise.reject(e));
    return Promise.reject(e);
  });
};

export const fetchPackageRecording = fetchPart(skillfitPackageRecording);
export const fetchPackageResume = fetchPart(skillfitPackageResume);
